import { useState } from "react"
import { useNavigate } from "react-router-dom"
import { useTranslation } from "react-i18next"
import axios from "axios"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { API_BASE } from "@/config/api"
import { CLINICAL_VERTICALS } from "@/config/clinical-verticals"
import { toast } from "sonner"

const API = API_BASE

export function OnboardingWizard({ onDone }) {
  const { t } = useTranslation()
  const [step, setStep] = useState(0)
  const [practice, setPractice] = useState({ name: "", cui: "", address: "" })
  const [primaryVertical, setPrimaryVertical] = useState("psych")
  const [extraVerticals, setExtraVerticals] = useState([])
  const navigate = useNavigate()

  const enabledVerticals = [
    primaryVertical,
    ...extraVerticals.filter((v) => v !== primaryVertical),
  ]

  const toggleExtra = (value) => {
    setExtraVerticals((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
    )
  }

  const steps = [
    async () => {
      await axios.patch(`${API}/settings`, { practice })
      setStep(1)
    },
    async () => {
      await axios.patch(`${API}/settings`, {
        clinical: {
          primary_vertical: primaryVertical,
          enabled_verticals: enabledVerticals,
          locale: "ro",
        },
      })
      await axios.post(`${API}/note-templates/seed`, { verticals: enabledVerticals })
      setStep(2)
    },
    async () => {
      await axios.post(`${API}/integrations/google_calendar/connect`)
      onDone?.()
      toast.success(t("app.onboarding.toastDone"))
      navigate("/app")
    },
  ]

  const next = async () => {
    try {
      await steps[step]()
    } catch {
      toast.error(t("app.onboarding.toastStepFailed"))
      if (step < 2) setStep(step + 1)
      else onDone?.()
    }
  }

  return (
    <div className="fixed inset-0 z-50 bg-background/95 flex items-center justify-center p-6">
      <div className="max-w-lg w-full rounded-2xl border border-border bg-card p-8 max-h-[90vh] overflow-y-auto">
        <p className="text-xs uppercase tracking-wider text-muted-foreground">
          {t("app.onboarding.step", { current: step + 1, total: 3 })}
        </p>
        {step === 0 && (
          <>
            <h2 className="font-display text-2xl mt-2">{t("app.onboarding.practiceTitle")}</h2>
            <div className="mt-4 space-y-3">
              <div><Label>{t("app.onboarding.name")}</Label><Input value={practice.name} onChange={(e) => setPractice({ ...practice, name: e.target.value })} /></div>
              <div><Label>{t("app.onboarding.cui")}</Label><Input value={practice.cui} onChange={(e) => setPractice({ ...practice, cui: e.target.value })} /></div>
              <div><Label>{t("app.onboarding.address")}</Label><Input value={practice.address} onChange={(e) => setPractice({ ...practice, address: e.target.value })} /></div>
            </div>
          </>
        )}
        {step === 1 && (
          <>
            <h2 className="font-display text-2xl mt-2">{t("app.onboarding.specialtyTitle")}</h2>
            <p className="text-sm text-muted-foreground mt-2">{t("app.onboarding.specialtyBody")}</p>
            <div className="mt-4 space-y-2">
              {CLINICAL_VERTICALS.map((v) => (
                <label key={v.value} className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="radio" name="primary_vertical" checked={primaryVertical === v.value} onChange={() => setPrimaryVertical(v.value)} />
                  {v.label}
                </label>
              ))}
            </div>
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mt-6 mb-2">{t("app.onboarding.alsoOffer")}</p>
            <div className="space-y-1">
              {CLINICAL_VERTICALS.filter((v) => v.value !== primaryVertical).map((v) => (
                <label key={v.value} className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="checkbox" checked={extraVerticals.includes(v.value)} onChange={() => toggleExtra(v.value)} />
                  {v.label}
                </label>
              ))}
            </div>
          </>
        )}
        {step === 2 && (
          <>
            <h2 className="font-display text-2xl mt-2">{t("app.onboarding.calendarTitle")}</h2>
            <p className="text-sm text-muted-foreground mt-2">{t("app.onboarding.calendarBody")}</p>
          </>
        )}
        <Button className="w-full rounded-full mt-6" onClick={next}>
          {step < 2 ? t("app.onboarding.continue") : t("app.onboarding.openDashboard")}
        </Button>
        <button type="button" className="w-full text-xs text-muted-foreground mt-3" onClick={onDone}>{t("app.onboarding.skip")}</button>
      </div>
    </div>
  )
}
